import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import ActionBar from 'components/text_editor_new/components/action_bar/action_bar';
import { Attachment, Emoji } from 'components/composer/shared/editor_action_buttons_new';
import {
  AnswerPanelControl,
  BoldItalicUnderlineControls,
  ListControls,
  Separator,
} from 'components/composer/shared/editor_controls_new';
import { EditorContainer } from 'components/composer/shared/editor_new';
import { LinkButton } from 'components/composer/inline_links/link_button';
import { LinkEditor } from 'components/composer/inline_links/link_editor';
import AddTask from 'actions/composition/add_task';
import { AgentMentionMenu, useCreateAgentMentionPlugin } from 'components/text_editor_new/plugins/agent_mention';
import AssigneeSelector from 'components/composer/shared/assignee_selector';
import Composer, { Header, NewHeaderText } from './shared/composer_new';
import ComposerEditor from 'components/composer/shared/composer_editor';
import DatePicker from 'components/composer/shared/date_picker';
import EditorErrors from 'components/composer/shared/editor_errors';
import EditTask from 'actions/composer/edit_task';
import FontColorControls from 'components/composer/shared/font_color_controls';
import { isMacOS } from 'scripts/lib/browser_detector';
import TableMenu from 'components/composer/shared/editor_table_controls_new';
import TaskTopicsRow from 'components/composer/shared/task_topics_row';
import TextAlignmentMenu from 'components/composer/shared/editor_alignment_controls_new';
import useComposer from 'components/composer/shared/use_composer';
import useEditor from 'components/composer/shared/use_editor';
import useRichTextEditorPlugins from 'components/text_editor_new/hooks/use_rich_text_editor_plugins';
import serializeHtmlExternal from 'components/text_editor_new/lib/serialize_html_external';
import { useSubmitOnHotkey } from 'components/composer/shared/use_submit_on_hotkey';

const LeftWrapper = styled.div`
  display: flex;
`;

const taskForCreateOrEdit = isEditing => (isEditing ? EditTask : AddTask);

export function TaskComposer({ isEditing }) {
  const { t } = useTranslation();
  const submitText = isEditing ? t('Save Task') : t('Add Task');
  const headerText = isEditing ? t('Edit Task') : t('Create Task');
  const composerProps = useComposer({
    action: taskForCreateOrEdit(isEditing),
    options: { trimEmptyParagraphs: true, trimParagraphWhitespace: true, createContent: addMentionedAgentIds },
  });
  const editorProps = useEditor('bodyHtml', composerProps, { serializer: serializeHtmlExternal });

  const { agentMentionPlugins, menuActionsRef } = useCreateAgentMentionPlugin();
  const editorRef = useRichTextEditorPlugins(composerProps, editorProps, agentMentionPlugins, {
    addTrailingParagraphAfterTable: true,
  });

  // Register keyboard hotkeys that result in submitting the composition. For historic reason, we use both Meta+Enter
  // and Ctrl+Enter on Mac and just Ctrl+Enter on Windows
  const keyboardHotkeys = ['Mod+Enter'];
  if (isMacOS()) keyboardHotkeys.push('Control+Enter');
  useSubmitOnHotkey(editorRef.current, editorProps.onSubmit, keyboardHotkeys);

  return (
    <Composer
      defaultEditor={editorRef.current}
      defaultEditorId={editorProps.editorId}
      defaultOnChange={editorProps.onChange}
      excludeErrors
      includeAttachments
      initialHtml={editorProps.initialHtml}
      submitText={submitText}
      {...composerProps}
    >
      <Header>
        <LeftWrapper>
          <NewHeaderText>{headerText}</NewHeaderText>
          <DatePicker />
        </LeftWrapper>
        <AssigneeSelector />
      </Header>

      <TaskTopicsRow isEditing={isEditing} />

      <EditorContainer excludeAnswerMenuButton includeAttachments>
        <ComposerEditor placeholder={t('Type to enter task description')} {...editorProps} />
        <AgentMentionMenu menuActionsRef={menuActionsRef} />
        <LinkEditor hotKey="mod+k" />
      </EditorContainer>

      <ActionBar>
        <>
          <AnswerPanelControl />
        </>
        <BoldItalicUnderlineControls {...editorProps} />
        <FontColorControls {...editorProps} />
        <TextAlignmentMenu {...editorProps} />
        <ListControls {...editorProps} />
        <TableMenu {...editorProps} />
        <>
          <Separator />
          <LinkButton />
        </>
        <Emoji {...editorProps} />
        <Attachment />
      </ActionBar>

      <EditorErrors />
    </Composer>
  );
}

TaskComposer.propTypes = {
  isEditing: PropTypes.bool,
};

// Attaches the agents mentioned in the task so AddTask can convert them to mentions and notify them.
function addMentionedAgentIds(editor, attr, content) {
  if (!editor.getMentionedAgentIds) {
    return content;
  }
  return { ...content, mentionedAgentIds: editor.getMentionedAgentIds() };
}

export default React.memo(TaskComposer);
